import React, { useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  IconButton,
  Modal,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import useSWR from 'swr';
import Api from '../../api';
import {
  mainColors,
  secondaryColors,
  alertColors,
  successColors
} from '../../theme/themeConfig';

const ModalContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  boxShadow: '0 20px 60px rgba(15,28,46,.18)',
  display: 'flex',
  flexDirection: 'column',
  left: '50%',
  maxHeight: '85vh',
  overflowY: 'auto',
  padding: '32px 36px',
  position: 'absolute' as const,
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 520
}));

const Label = styled(Typography)(() => ({
  color: '#374151',
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '.3px',
  marginBottom: '4px',
  textTransform: 'uppercase'
}));

const EntryRow = styled(Box)(() => ({
  alignItems: 'center',
  background: '#F8FAFC',
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: '8px',
  display: 'flex',
  gap: '10px',
  justifyContent: 'space-between',
  marginBottom: '8px',
  padding: '10px 14px'
}));

interface Props {
  clientId: string;
  clientName: string;
  onClose: () => void;
}

const WhitelistModal: React.FC<Props> = function WhitelistModal({
  clientId,
  clientName,
  onClose
}) {
  const [newEmail, setNewEmail] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');

  const {
    data: entries = [],
    error: whitelistError,
    mutate
  } = useSWR(`sr-whitelist-${clientId}`, () =>
    Api.ServiceRequestApi.getWhitelist(clientId)
  );

  const isLoading = !entries.length && !whitelistError;

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAdd = async () => {
    if (!isValidEmail(newEmail.trim())) {
      setSnackMsg('Enter a valid email address');
      return;
    }
    setAdding(true);
    try {
      await Api.ServiceRequestApi.addWhitelistEntry({
        clientId,
        email: newEmail.trim().toLowerCase(),
        label: newLabel.trim() || undefined
      });
      setNewEmail('');
      setNewLabel('');
      await mutate();
      setSnackMsg('Email added to whitelist');
    } catch (e: any) {
      setSnackMsg(e?.response?.data?.message || 'Failed to add email');
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (id: string, active: boolean) => {
    try {
      await Api.ServiceRequestApi.updateWhitelistEntry(id, { active });
      await mutate();
    } catch {
      setSnackMsg('Failed to update entry');
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await Api.ServiceRequestApi.removeWhitelistEntry(id);
      await mutate();
      setSnackMsg('Entry removed');
    } catch {
      setSnackMsg('Failed to remove entry');
    }
  };

  return (
    <>
      <Modal
        closeAfterTransition
        open
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 200 }}
        onClose={onClose}
      >
        <Fade in>
          <ModalContainer>
            {/* Header */}
            <Box
              sx={{
                alignItems: 'flex-start',
                display: 'flex',
                justifyContent: 'space-between',
                mb: '20px'
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>
                  SR Email Whitelist
                </Typography>
                <Typography
                  sx={{
                    color: secondaryColors.SECONDARY,
                    fontSize: '12px',
                    mt: '2px'
                  }}
                >
                  {clientName}
                </Typography>
              </Box>
              <IconButton onClick={onClose} size="small">
                <HighlightOffOutlinedIcon />
              </IconButton>
            </Box>

            {/* Info banner */}
            <Box
              sx={{
                background: mainColors.MAIN_LIGHT,
                border: `1px solid ${mainColors.MAIN_1}`,
                borderRadius: '8px',
                display: 'flex',
                gap: '10px',
                mb: '20px',
                p: '12px 14px'
              }}
            >
              <MailOutlineIcon
                sx={{
                  color: mainColors.MAIN,
                  flexShrink: 0,
                  fontSize: '18px',
                  mt: '1px'
                }}
              />
              <Typography
                sx={{
                  color: mainColors.MAIN_5,
                  fontSize: '12px',
                  lineHeight: 1.5
                }}
              >
                Only emails on this list can create Service Requests for{' '}
                <strong>{clientName}</strong> by emailing{' '}
                <strong>sr@mybluberry.com</strong>. Unknown senders are
                automatically rejected.
              </Typography>
            </Box>

            {/* Add new entry */}
            <Label>Add Authorized Email</Label>
            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mb: '20px' }}
            >
              <TextField
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                placeholder="email@company.com"
                size="small"
                sx={{ flex: 2, minWidth: '160px' }}
                value={newEmail}
              />
              <TextField
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                placeholder="Label (optional)"
                size="small"
                sx={{ flex: 1, minWidth: '120px' }}
                value={newLabel}
              />
              <Button
                disabled={adding || !newEmail.trim()}
                onClick={handleAdd}
                startIcon={
                  adding ? (
                    <CircularProgress size={14} sx={{ color: '#fff' }} />
                  ) : (
                    <AddIcon />
                  )
                }
                sx={{
                  '&:disabled': { opacity: 0.6 },
                  '&:hover': { background: mainColors.MAIN_3 },
                  background: mainColors.MAIN,
                  color: '#fff',
                  flexShrink: 0,
                  fontSize: '12px'
                }}
                variant="contained"
              >
                Add
              </Button>
            </Box>

            {/* Existing entries */}
            <Label>
              Authorized Senders
              {entries.length > 0 && (
                <span
                  style={{
                    background: mainColors.MAIN_LIGHT,
                    borderRadius: '10px',
                    color: mainColors.MAIN,
                    fontSize: '11px',
                    fontWeight: 700,
                    marginLeft: '8px',
                    padding: '1px 8px'
                  }}
                >
                  {entries.length}
                </span>
              )}
            </Label>

            {isLoading && (
              <Box
                sx={{ display: 'flex', justifyContent: 'center', py: '24px' }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            {!isLoading && entries.length === 0 && (
              <Box
                sx={{
                  border: `1px dashed ${secondaryColors.SECONDARY_6}`,
                  borderRadius: '8px',
                  color: '#CBD5E1',
                  fontSize: '12px',
                  mb: '8px',
                  py: '24px',
                  textAlign: 'center'
                }}
              >
                No authorized senders yet
              </Box>
            )}
            {!isLoading &&
              entries.length > 0 &&
              entries.map((entry) => (
                <EntryRow key={entry.id}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{ alignItems: 'center', display: 'flex', gap: '6px' }}
                    >
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {entry.email}
                      </Typography>
                      {!entry.active && (
                        <Chip
                          label="Disabled"
                          size="small"
                          sx={{
                            background: alertColors.ALERT_2,
                            color: '#fff',
                            fontSize: '9px',
                            fontWeight: 700,
                            height: '16px'
                          }}
                        />
                      )}
                    </Box>
                    {entry.label && (
                      <Typography
                        sx={{
                          color: secondaryColors.SECONDARY,
                          fontSize: '11px',
                          mt: '1px'
                        }}
                      >
                        {entry.label}
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{ alignItems: 'center', display: 'flex', gap: '4px' }}
                  >
                    <Tooltip title={entry.active ? 'Disable' : 'Enable'}>
                      <Switch
                        checked={entry.active}
                        onChange={(e) =>
                          handleToggle(entry.id, e.target.checked)
                        }
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: successColors.SUCCESS
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                            {
                              backgroundColor: successColors.SUCCESS
                            }
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton
                        onClick={() => handleRemove(entry.id)}
                        size="small"
                        sx={{ color: alertColors.ALERT }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </EntryRow>
              ))}

            {/* Footer */}
            <Box
              sx={{ display: 'flex', justifyContent: 'flex-end', mt: '20px' }}
            >
              <Button onClick={onClose} variant="outlined">
                Done
              </Button>
            </Box>
          </ModalContainer>
        </Fade>
      </Modal>

      <Snackbar
        autoHideDuration={3000}
        message={snackMsg}
        onClose={() => setSnackMsg('')}
        open={Boolean(snackMsg)}
      />
    </>
  );
};

export default WhitelistModal;
